/**
 * Segnalazioni sul catalogo condiviso ("richiedi modifica").
 *
 * L'organizzazione segnala un problema su un materiale di catalogo (prezzo
 * errato, dato mancante, prodotto fuori produzione...); la segnalazione arriva
 * all'admin di piattaforma che la lavora. È controllo qualità del catalogo
 * fatto da chi lo usa ogni giorno.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';

export type ChangeRequestType = 'prezzo' | 'dato_tecnico' | 'fuori_produzione' | 'nuovo_prodotto' | 'altro';
export type ChangeRequestStatus = 'nuova' | 'in_lavorazione' | 'risolta' | 'rifiutata';

export interface MaterialChangeRequest {
  id: string;
  organization_id: string;
  material_id: string;
  request_type: ChangeRequestType;
  message: string;
  suggested_value: string | null;
  status: ChangeRequestStatus;
  admin_note: string | null;
  created_at: string;
  resolved_at: string | null;
}

export const CHANGE_REQUEST_TYPE_LABEL: Record<ChangeRequestType, string> = {
  prezzo: 'Prezzo errato',
  dato_tecnico: 'Dato tecnico errato o mancante',
  fuori_produzione: 'Prodotto fuori produzione',
  nuovo_prodotto: 'Prodotto da aggiungere',
  altro: 'Altro',
};

export const CHANGE_REQUEST_STATUS_LABEL: Record<ChangeRequestStatus, string> = {
  nuova: 'Nuova',
  in_lavorazione: 'In lavorazione',
  risolta: 'Risolta',
  rifiutata: 'Rifiutata',
};

/** Crea una segnalazione per il materiale indicato. */
export const useCreateChangeRequest = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      materialId: string;
      requestType: ChangeRequestType;
      message: string;
      suggestedValue?: string | null;
    }) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione selezionata');
      if (!input.message.trim()) throw new Error('Descrivi brevemente il problema');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).from('material_change_requests').insert({
        organization_id: currentOrganizationId,
        material_id: input.materialId,
        request_type: input.requestType,
        message: input.message.trim(),
        suggested_value: input.suggestedValue?.trim() || null,
        created_by: user?.id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material-change-requests'] });
      toast.success('Segnalazione inviata', {
        description: 'Il team DryConfig la esaminerà e aggiornerà il catalogo.',
      });
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e
        ? String((e as { message: unknown }).message) : String(e);
      toast.error(`Invio non riuscito: ${msg}`);
    },
  });
};

/** Segnalazioni visibili all'utente (le proprie; tutte se admin di piattaforma). */
export const useChangeRequests = (statusFilter?: ChangeRequestStatus | 'all') =>
  useQuery({
    queryKey: ['material-change-requests', statusFilter ?? 'all'],
    staleTime: 30_000,
    queryFn: async (): Promise<Array<MaterialChangeRequest & { material_name?: string; material_code?: string; org_name?: string }>> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let q = (supabase as any)
        .from('material_change_requests')
        .select('*, materials(name, code), organizations(name)')
        .order('created_at', { ascending: false });
      if (statusFilter && statusFilter !== 'all') q = q.eq('status', statusFilter);
      const { data, error } = await q;
      if (error) throw error;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((data ?? []) as any[]).map((r) => ({
        ...r,
        material_name: r.materials?.name,
        material_code: r.materials?.code,
        org_name: r.organizations?.name,
      }));
    },
  });

/** Admin: aggiorna stato/nota di una segnalazione. */
export const useUpdateChangeRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status?: ChangeRequestStatus; adminNote?: string }) => {
      const patch: Record<string, unknown> = {};
      if (input.status) patch.status = input.status;
      if (input.adminNote !== undefined) patch.admin_note = input.adminNote;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('material_change_requests')
        .update(patch)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['material-change-requests'] });
      toast.success('Segnalazione aggiornata');
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e
        ? String((e as { message: unknown }).message) : String(e);
      toast.error(`Aggiornamento non riuscito: ${msg}`);
    },
  });
};
